import { useQuery } from '@apollo/client/react'
import { Flex, Text, useDisclosure } from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { DataTable } from '../../../components/DataTable'
import {
  smrtArtifactByVersionQuery,
  smrtArtifactsListQuery,
} from '../../../services/smrtArtifactService'
import {
  type SmrtArtifactRow,
  SmrtArtifactViewModal,
  useSmrtArtifactsAdminColumns,
} from './SmrtArtifactsAdminPage.parts'

export const SmrtArtifactsAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: artifactsData, loading: isLoading } = useQuery(
    smrtArtifactsListQuery,
    { fetchPolicy: 'network-only' },
  )
  const artifacts = ((
    artifactsData as { SmrtArtifact_list?: { items?: unknown[] } }
  )?.SmrtArtifact_list?.items ?? []) as SmrtArtifactRow[]

  const viewModal = useDisclosure()
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null)
  const versionFromUrl = searchParams.get('version')

  const { data: fullArtifactData, loading: loadingFull } = useQuery(
    smrtArtifactByVersionQuery,
    {
      variables: { smrtVersion: selectedVersion ?? '' },
      skip: !selectedVersion || !viewModal.isOpen,
    },
  )
  const fullArtifact =
    (fullArtifactData as { smrtArtifactByVersion?: unknown } | undefined)
      ?.smrtArtifactByVersion ?? null

  const handleView = useCallback(
    (smrtVersion: string) => {
      setSelectedVersion(smrtVersion)
      viewModal.onOpen()
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.set('version', smrtVersion)
        return next
      })
    },
    [setSearchParams, viewModal],
  )

  const handleCloseView = () => {
    viewModal.onClose()
    setSelectedVersion(null)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('version')
      return next
    })
  }

  const viewJson =
    fullArtifact != null
      ? JSON.stringify(
          {
            smrtIri: (fullArtifact as { smrtIri?: string }).smrtIri,
            smrtVersion: (fullArtifact as { smrtVersion?: string }).smrtVersion,
            schemaId: (fullArtifact as { schemaId?: string }).schemaId,
            schema: (fullArtifact as { schema?: unknown }).schema,
            models: (fullArtifact as { models?: unknown }).models,
            fields: (fullArtifact as { fields?: unknown }).fields,
          },
          null,
          2,
        )
      : ''

  const columns = useSmrtArtifactsAdminColumns({ onView: handleView })

  useEffect(() => {
    if (
      versionFromUrl &&
      artifacts.some(artifact => artifact.smrtVersion === versionFromUrl) &&
      selectedVersion !== versionFromUrl
    ) {
      setSelectedVersion(versionFromUrl)
      viewModal.onOpen()
    }
  }, [versionFromUrl, artifacts, selectedVersion, viewModal])

  return (
    <AdminPageLayout pageTitle="SMRT Artifacts">
      <Flex direction="column" gap="4" pb="32px">
        <Flex justify="space-between" align="center">
          <Text variant="sectionHeader">SMRT Artifacts</Text>
        </Flex>
        <Text fontSize="sm" color="gray.600">
          Read-only view of SMRT versions in blob storage. Select a version to
          inspect its schema.prisma.json contents.
        </Text>

        {isLoading ? (
          <Text>Loading...</Text>
        ) : artifacts.length === 0 ? (
          <Text color="gray.600">No SMRT versions found in blob storage.</Text>
        ) : (
          <DataTable
            data={artifacts}
            columns={columns}
            isLoading={isLoading}
            height="calc(100vh - 200px)"
          />
        )}
      </Flex>

      <SmrtArtifactViewModal
        isOpen={viewModal.isOpen}
        onClose={handleCloseView}
        loadingFull={loadingFull}
        viewJson={viewJson}
        smrtVersion={selectedVersion}
      />
    </AdminPageLayout>
  )
}
